import { TranslationDto } from '../translation.dto'

/**
 * FR-04-BR-02 — Reward type.
 * Khớp enum `challenge_reward_option_type` (`ChallengeRewardOption.type`).
 * Implements: FEAT-001#AC-001, AC-001
 * Tests: src/app/+governance-admin/+stamp-collection/edit-challenge/edit-challenge.validation.spec.ts
 */
export const REWARD_TYPE = {
  PHYSICAL: 'PHYSICAL',
  VIRTUAL: 'VIRTUAL',
  EC_VOUCHER: 'EC_VOUCHER'
} as const

export type RewardType = typeof REWARD_TYPE[keyof typeof REWARD_TYPE]

/**
 * FR-04-BR-03 — loại giảm giá của Virtual Gift / EC Voucher.
 * Khớp enum `challenge_reward_discount_type` (`ChallengeRewardOption.discountType`).
 */
export const REWARD_DISCOUNT_TYPE = {
  PERCENTAGE: 'PERCENTAGE',
  FIXED_AMOUNT: 'FIXED_AMOUNT'
} as const

export type RewardDiscountType = typeof REWARD_DISCOUNT_TYPE[keyof typeof REWARD_DISCOUNT_TYPE]

/**
 * UC1 — Product type của EC voucher ('physical' | 'virtual' theo contract BE).
 * Implements: FEAT-001#AC-004, AC-004
 * Tests: src/app/+governance-admin/+stamp-collection/edit-challenge/edit-challenge.validation.spec.ts
 */
export const REWARD_PRODUCT_TYPE = {
  PHYSICAL: 'physical',
  VIRTUAL: 'virtual'
} as const

export type RewardProductType = typeof REWARD_PRODUCT_TYPE[keyof typeof REWARD_PRODUCT_TYPE]

/**
 * UC1 — Coupon type (loại áp dụng) của EC voucher ('order' | 'delivery' | 'tenant-specific' theo contract BE).
 * Implements: FEAT-001#AC-007, AC-007
 * Tests: src/app/+governance-admin/+stamp-collection/edit-challenge/edit-challenge.validation.spec.ts
 */
export const REWARD_COUPON_TYPE = {
  ORDER: 'order',
  DELIVERY: 'delivery',
  TENANT_SPECIFIC: 'tenant-specific'
} as const

export type RewardCouponType = typeof REWARD_COUPON_TYPE[keyof typeof REWARD_COUPON_TYPE]

/**
 * FR-04-BR-06 — nguồn Reward Code.
 * Khớp enum `challenge_reward_code_source` (`ChallengeRewardOption.codeSource`).
 */
export const REWARD_CODE_SOURCE = {
  UPLOADED: 'UPLOADED',
  AUTO_GENERATED: 'AUTO_GENERATED'
} as const

export type RewardCodeSource = typeof REWARD_CODE_SOURCE[keyof typeof REWARD_CODE_SOURCE]

/**
 * Container **chỉ dùng ở FE** để giữ file code vừa upload + validate (API #3) cho một
 * Reward Option, chờ tới lúc Save Challenge.
 *
 * ⚠️ Swagger 2026-08-25: BE **không nhận** object lồng `pool{codes,fileId}` như giả định ban
 * đầu (`API Design.md`). Wire format thật là 2 field phẳng ngay trên option:
 *   - Create (`CreateRewardOptionDto`) / Option mới thêm lúc Update: `quantity?`, `fileId?`
 *   - Option **đã tồn tại** lúc Update: đi qua mảng riêng `rewardQuantities[{id,quantity,fileId?}]`
 *     (xem `UpdateRewardQuantityDto`), KHÔNG gửi trong `options[]` nữa.
 * `codes: string[]` (nhập tay danh sách code) **không có** trong wire format thật — chỉ còn
 * đường `UPLOADED` qua file. `AdminChallengeService` đọc `pool.fileId` khi build body, bỏ
 * qua `codes`.
 */
export class ChallengeRewardPoolDto {
  public codes: string[]
  public fileId?: string

  public static create(data?: Partial<ChallengeRewardPoolDto>): ChallengeRewardPoolDto {
    const instance = new ChallengeRewardPoolDto()
    instance.codes = []
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * API #12 — phần tử của mảng `rewardQuantities[]`, dùng để đổi `quantity`/`fileId` của một
 * Reward Option **đã tồn tại** (`id` bắt buộc). BE: `quantity` bắt buộc, `0 ≤ quantity ≤ 10000`.
 *
 * ✅ ĐÃ CHỐT (xác nhận từ BE): option **đã có `id`** → `quantity`/`fileId` đi qua mảng này
 * (KHÔNG gửi trong `options[]`); option **mới** (không `id`) → gửi inline trong `options[]`
 * như Create. Upload file mới cho option đã tồn tại thì **cộng dồn** code vào pool cũ, không
 * thay thế — `quantity` gửi lên là **tổng cộng dồn** (cũ + mới), chỉ 1 `fileId` mỗi lần Save.
 * Xem `AdminChallengeService.toUpdateBody()` và `RewardOptionFormComponent.handleCodeFileSelect()`.
 */
export class UpdateRewardQuantityDto {
  public id: string
  public quantity: number
  public fileId?: string

  public static create(data?: Partial<UpdateRewardQuantityDto>): UpdateRewardQuantityDto {
    const instance = new UpdateRewardQuantityDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/** Body API #3 `POST /challenges/reward-codes/validate-import`. */
export class ValidateRewardCodeImportDto {
  /** File XLSX đã upload qua file service chung. */
  public fileId: string
  /** Chỉ truyền khi Edit — để BE kiểm capacity của Reward Option đang tồn tại. */
  public rewardOptionId?: string

  public static create(data?: Partial<ValidateRewardCodeImportDto>): ValidateRewardCodeImportDto {
    const instance = new ValidateRewardCodeImportDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * Response API #3 — FR-04-BR-06. Không persist gì, chỉ validate.
 *
 * ✅ ĐÃ CHỐT:
 *   - Có bất kỳ code lỗi/trùng nào → **chặn Save toàn bộ**, không cho nhập riêng phần
 *     `validCodes` còn lại.
 *   - Hiển thị tổng số (`totalCodes`/`validCodes`/`duplicateCount`/`invalidFormatCount`) +
 *     tối đa `REWARD_CODE_IMPORT_PREVIEW_LIMIT` (20) code mỗi nhóm lỗi từ `duplicateCodes[]`/
 *     `invalidFormatCodes[]` (BE trả full mảng, FE tự cắt bớt khi hiển thị).
 *   - `message` dùng làm fallback text khi hiển thị lỗi, không tự dựng lại từ các con số.
 * Xem `RewardOptionFormComponent.handleCodeFileSelect()`.
 */
export class RewardCodeImportResultDto {
  public success?: boolean
  public totalCodes?: number
  /** Số code hợp lệ, unique, còn trong capacity. */
  public validCodes?: number
  public duplicateCount?: number
  public duplicateCodes?: string[]
  public invalidFormatCount?: number
  public invalidFormatCodes?: string[]
  public message?: string

  public static create(data?: Partial<RewardCodeImportResultDto>): RewardCodeImportResultDto {
    const instance = new RewardCodeImportResultDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * Bảng `ChallengeRewardCodeFile` — lịch sử upload file code của một Reward Option.
 *
 * ⚠️ BE-MISSING N3: API #10 **không expose** mảng này. Chỉ còn dùng được ở phía client
 * (giữ file vừa upload trong form). Nếu design FR-04 có hiển thị lịch sử upload thì
 * cần BE bổ sung `options[].codeFiles[]` vào API #10.
 */
export class ChallengeRewardCodeFileDto {
  public id?: string
  public fileId?: string
  public fileName?: string
  /** `codeCount` — số code hợp lệ unique được chấp nhận từ file. */
  public codeCount?: number
  public uploadedBy?: string
  public uploadedByName?: string
  /** ISO Date string. */
  public createdAt?: string

  public static create(data?: Partial<ChallengeRewardCodeFileDto>): ChallengeRewardCodeFileDto {
    const instance = new ChallengeRewardCodeFileDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/**
 * Bảng `ChallengeRewardOption` + `ChallengeRewardOptionTranslation` + 2 bảng liên kết.
 *
 * ✅ API #10 khớp **13/13** field nghiệp vụ: `id`, `type`, `imageId`, `numberOfRewards`,
 * `codeSource`, `discountType`, `discountValue`, `minimumPurchaseValue`,
 * `maximumDiscountValue`, `validityStartAt`, `validityEndAt`, `mallIds`,
 * `offlineTenantIds`.
 * `translations[]` có `name` + `description` — description thêm theo FR-04-BR-03 / FR-04-BR-05.
 */
export class ChallengeRewardOptionDto {
  public id?: string
  public type?: RewardType
  /** `ChallengeRewardOptionTranslation.name`. */
  public name?: TranslationDto
  /**
   * `ChallengeRewardOptionTranslation.description` — FR-04-BR-03 (Virtual) / FR-04-BR-05 (Physical),
   * EN + KH, bắt buộc khi Challenge chưa chạy (xem `validateRewardOption`).
   */
  public description?: TranslationDto
  public imageId?: string
  public imageUrl?: string
  /** `numberOfRewards` NOT NULL (> 0). Khi codeSource = UPLOADED, BE lấy theo số code. */
  public numberOfRewards?: number
  public codeSource?: RewardCodeSource
  /**
   * `ChallengeRewardCodeFile` — client-side only (xem `ChallengeRewardCodeFileDto`).
   * ⚠️ API #10 không trả field này.
   */
  public codeFiles?: ChallengeRewardCodeFileDto[]
  /**
   * Nguồn code gửi lên khi Save (API #11, #12). Chỉ có ở **request**, BE không trả về.
   * `AdminChallengeService` dựng từ `codeFiles` + `codeSource`.
   */
  public pool?: ChallengeRewardPoolDto
  /** `ChallengeRewardOptionMall` — Mall áp dụng, theo Mall của Challenge. */
  public mallIds?: string[]

  /** Chỉ Virtual Gift / EC Voucher (FR-04-BR-03, UC1). */
  public discountType?: RewardDiscountType
  public discountValue?: number
  public minimumPurchaseValue?: number
  /** Chỉ hiển thị khi discountType = PERCENTAGE của Virtual Gift (FR-04-BR-03). */
  public maximumDiscountValue?: number
  /** ISO Date string. */
  public validityStartAt?: string
  public validityEndAt?: string
  /** `ChallengeRewardOptionOfflineTenant` — nơi được dùng quà (FK `OfflineTenant`). */
  public offlineTenantIds?: string[]

  /**
   * UC1 — các trường riêng của EC voucher theo contract BE.
   * Implements: FEAT-001#AC-004, FEAT-001#AC-005, FEAT-001#AC-006, FEAT-001#AC-007, AC-004, AC-005, AC-006, AC-007
   */
  public productType?: RewardProductType
  public maximumPurchaseValue?: number
  public minimumNumberOfTenants?: number
  public maximumNumberOfTenants?: number
  public couponType?: RewardCouponType
  /** Gian hàng Online (FK `Tenant`) khi `couponType === REWARD_COUPON_TYPE.TENANT_SPECIFIC`. */
  public tenantId?: string | null

  /**
   * FR-04-BR-08 — không được giảm `numberOfRewards` xuống dưới số đã phát.
   *
   * ⚠️ BE-MISSING: API #10 **không trả** field này ⇒ FE không validate được client-side.
   * Thay thế: `claimed + used` của API #4 (Reward Summary) theo `rewardOptionId`.
   */
  public issuedCount?: number

  public static create(data?: Partial<ChallengeRewardOptionDto>): ChallengeRewardOptionDto {
    const instance = new ChallengeRewardOptionDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}

/** Bảng `ChallengeRewardTier`. */
export class ChallengeRewardTierDto {
  public id?: string
  /**
   * `ChallengeRewardTier.position` (> 0, unique theo challenge) = cấp độ reward,
   * cũng là thứ tự ưu tiên phát quà (FR-04-BR-07).
   */
  public position?: number
  public options?: ChallengeRewardOptionDto[]

  public static create(data?: Partial<ChallengeRewardTierDto>): ChallengeRewardTierDto {
    const instance = new ChallengeRewardTierDto()
    if (data) {
      Object.assign(instance, data)
    }
    return instance
  }
}
